import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, CheckCheck, Rocket, TrendingUp, Sparkles, FileText, CreditCard } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { useT } from "@/i18n";

const iconFor = (type) => ({
  performance: TrendingUp, pattern: Sparkles, data: Rocket, report: FileText, billing: CreditCard,
}[type] || Bell);

export const NotificationBell = () => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 60000,
  });
  const unread = data?.unread || 0;

  const markRead = async () => {
    await api.post("/notifications/read");
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-xl" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
              {unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <span className="font-semibold">{t("notifications.title")}</span>
          {unread > 0 && (
            <button onClick={markRead} className="flex items-center gap-1 text-xs text-brand-700 dark:text-brand-400" data-testid="mark-read-btn">
              <CheckCheck className="h-3.5 w-3.5" /> {t("notifications.markRead")}
            </button>
          )}
        </div>
        <ScrollArea className="max-h-80">
          {!data?.notifications?.length ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">{t("notifications.empty")}</div>
          ) : (
            data.notifications.map((n) => {
              const Icon = iconFor(n.type);
              return (
                <div key={n.notification_id} className={`flex gap-3 px-4 py-3 border-b border-border/60 ${!n.read ? "bg-accent/40" : ""}`}>
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-500/10 text-brand-700 dark:text-brand-400">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm leading-snug">{n.text}</p>
                    <span className="text-xs text-muted-foreground">{formatDateTime(n.created_at)}</span>
                  </div>
                </div>
              );
            })
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
